<template>
  <div class="insight-visualization">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading insight data...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <strong>Error:</strong> {{ error }}
    </div>

    <!-- Visualization -->
    <div v-else-if="insightData">
      <!-- Bar Chart -->
      <insight-bar-chart
        v-if="insight.type === 'bar_chart'"
        :data="insightData"
        :height="400"
      />

      <!-- Line Chart -->
      <insight-line-chart
        v-else-if="insight.type === 'line_chart'"
        :data="insightData"
        :height="400"
      />

      <!-- Area Chart -->
      <insight-area-chart
        v-else-if="insight.type === 'area_chart'"
        :data="insightData"
        :height="400"
      />

      <!-- Table -->
      <insight-table
        v-else-if="insight.type === 'table'"
        :data="insightData"
      />

      <!-- Top Items -->
      <insight-top-items
        v-else-if="insight.type === 'top_items'"
        :data="insightData"
      />

      <!-- Metric -->
      <insight-metric
        v-else-if="insight.type === 'metric'"
        :data="insightData"
        :color="metricColor"
      />

      <!-- Unknown Type -->
      <div v-else class="alert alert-warning">
        Unknown visualization type: {{ insight.type }}
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5 text-muted">
      <i class="material-icons" style="font-size: 48px;">bar_chart</i>
      <p class="mt-2">No data to display</p>
    </div>
  </div>
</template>

<script>
import InsightBarChart from './InsightBarChart.vue';
import InsightLineChart from './InsightLineChart.vue';
import InsightAreaChart from './InsightAreaChart.vue';
import InsightTable from './InsightTable.vue';
import InsightTopItems from './InsightTopItems.vue';
import InsightMetric from './InsightMetric.vue';
import insightsAPI from '@/api/insights';

export default {
  name: 'InsightVisualization',
  components: {
    InsightBarChart,
    InsightLineChart,
    InsightAreaChart,
    InsightTable,
    InsightTopItems,
    InsightMetric,
  },
  props: {
    insight: {
      type: Object,
      required: true,
    },
    startTime: {
      type: String,
      default: null,
    },
    endTime: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      insightData: null,
      loading: false,
      error: null,
      metricColor: '#007bff',
    };
  },
  watch: {
    insight: {
      deep: true,
      handler() {
        this.loadData();
      },
    },
    startTime() {
      this.loadData();
    },
    endTime() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      if (!this.insight || !this.insight.id) {
        return;
      }

      this.loading = true;
      this.error = null;

      insightsAPI.executeInsight(this.insight.id, this.startTime, this.endTime)
        .then((response) => {
          this.insightData = response.data;
        })
        .catch((err) => {
          console.error('Failed to execute insight:', err);
          this.error = err.response && err.response.data && err.response.data.error || err.message || 'Failed to load insight data';
          // Fallback to mock data for development
          this.insightData = this.generateMockData();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generateMockData() {
      // Generate mock data based on insight type and configuration
      const type = this.insight.type;
      const splits = this.insight.splits || [];

      if (type === 'bar_chart' || type === 'line_chart' || type === 'area_chart') {
        const labels = splits.length > 0
          ? ['Scenario A', 'Scenario B', 'Scenario C', 'Scenario D', 'Scenario E']
          : ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'];

        return {
          labels,
          datasets: [{
            label: this.insight.name || 'Data',
            data: labels.map(() => Math.floor(Math.random() * 1000) + 100),
            backgroundColor: 'rgba(0, 123, 255, 0.5)',
            borderColor: 'rgba(0, 123, 255, 1)',
            borderWidth: 2,
          }],
        };
      }

      if (type === 'table') {
        return {
          headers: splits.length > 0 ? ['Scenario', 'Value'] : ['Date', 'Value'],
          rows: Array.from({ length: 5 }, (_, i) => [
            splits.length > 0 ? `Scenario ${String.fromCharCode(65 + i)}` : `2024-01-${String(i + 1).padStart(2, '0')}`,
            Math.floor(Math.random() * 1000) + 100,
          ]),
        };
      }

      if (type === 'top_items') {
        return {
          items: Array.from({ length: 10 }, (_, i) => ({
            id: `item-${i + 1}`,
            label: `Item ${i + 1}`,
            value: Math.floor(Math.random() * 1000) + 100,
            description: `Description for item ${i + 1}`,
          })),
        };
      }

      if (type === 'metric') {
        return {
          value: Math.floor(Math.random() * 10000) + 1000,
          label: this.insight.name || 'Metric',
          trend: Math.floor(Math.random() * 20) - 10,
        };
      }

      return null;
    },
  },
};
</script>

<style scoped>
.insight-visualization {
  min-height: 200px;
}
</style>

